import { useEffect } from "react";
import { Link } from "react-router-dom";
import { m as motion } from "framer-motion";
import { ArrowRight, Calendar, Mail } from "lucide-react";
import { buildWebpSrcSet } from "@/lib/image-srcset";
import { fadeIn, fadeInUp, staggerContainer, defaultViewport } from "@/lib/motion";
import { useSEO } from "@/lib/use-seo";
import {
  getWorkshopPath,
  PAST_WORKSHOPS,
  UPCOMING_WORKSHOPS,
  type Workshop,
} from "@/lib/workshops";

const summarize = (workshop: Workshop) =>
  workshop.seoDescription ?? workshop.shortDescription;

const pastWorkshopCtaLabel = (workshop: Workshop, hasDetailPage: boolean) => {
  if (!hasDetailPage) return "Access full recording";
  if (workshop.videoHighlights?.length) return "Watch highlight clips";
  return "Workshop details";
};

const Workshops = () => {
  useSEO({
    title: "Free AI for HR Workshops",
    description:
      "Join free live workshops where we apply AI for HR principles to build real solutions. Expert-led sessions with full recordings available to members.",
    path: "/workshops",
  });

  return (
    <div className="min-h-screen pt-32">
      {/* Hero */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="py-10 md:py-14 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-mesh opacity-30 pointer-events-none" />
        <div className="section-inner text-center relative z-10 max-w-3xl mx-auto">
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-6"
          >
            Free Live Workshops
          </motion.span>
          <motion.h1
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5"
          >
            AI for HR <span className="text-gradient">Workshops</span>
          </motion.h1>
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-muted-foreground mb-4"
          >
            Where we take the principles from the AI for HR Mastermind and apply
            them to build something or solve a problem.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-sm text-muted-foreground">
            Free to attend live · Expert-led, 60 minutes · Full recordings
            available to members
          </motion.p>
        </div>
      </motion.section>

      {/* Upcoming Workshops */}
      {UPCOMING_WORKSHOPS.length > 0 && (
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          className="py-10 md:py-16"
        >
          <div className="section-inner">
            <motion.h2
              variants={fadeInUp}
              className="text-2xl md:text-3xl font-bold mb-6"
            >
              Upcoming
            </motion.h2>
            <div className="grid gap-6">
              {UPCOMING_WORKSHOPS.map((workshop) => (
                <UpcomingWorkshopCard key={workshop.id} workshop={workshop} />
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* Past Workshops */}
      {PAST_WORKSHOPS.length > 0 && (
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          className="py-14 md:py-20 bg-muted/30"
        >
          <div className="section-inner">
            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap items-baseline justify-between gap-3 mb-6"
            >
              <h2 className="text-2xl md:text-3xl font-bold">Past workshops</h2>
              <span className="text-sm text-muted-foreground">
                Full recordings available to members
              </span>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PAST_WORKSHOPS.map((workshop) => (
                <PastWorkshopCard key={workshop.id} workshop={workshop} />
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* Newsletter CTA */}
      <WorkshopNewsletterSignup />

    </div>
  );
};

const WorkshopNewsletterSignup = () => {
  useEffect(() => {
    const scriptId = "convertkit-script";
    if (document.getElementById(scriptId)) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://f.convertkit.com/ckjs/ck.5.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="py-16 md:py-20"
    >
      <div className="section-inner">
        <motion.div
          variants={fadeIn}
          className="glass-strong rounded-3xl p-8 md:p-12 text-center"
        >
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-5">
              <Mail className="h-4 w-4" aria-hidden="true" />
              Stay in the loop
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Get Notified About Upcoming Workshops
            </h2>
            <p className="text-muted-foreground mb-8">
              New workshops are announced first to newsletter subscribers.
              Drop your email and we&apos;ll let you know as soon as the next one
              is scheduled.
            </p>

            <form
              action="https://app.kit.com/forms/7907819/subscriptions"
              method="post"
              data-sv-form="7907819"
              data-uid="c42cccfdf8"
              data-format="inline"
              data-version="5"
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                className="flex-1 px-4 py-3 rounded-xl bg-background/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                name="email_address"
                placeholder="your@email.com"
                required
                type="email"
                aria-label="Email address"
              />
              <button
                data-element="submit"
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold shadow-primary transition-all duration-200 hover:shadow-glow-primary hover:-translate-y-0.5 active:translate-y-0 active:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 disabled:pointer-events-none motion-reduce:transform-none"
              >
                Notify Me
              </button>
            </form>

            <p className="text-xs text-muted-foreground mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

const UpcomingWorkshopCard = ({ workshop }: { workshop: Workshop }) => {
  const detailPath = getWorkshopPath(workshop.slug);

  return (
    <motion.article variants={fadeInUp}>
      <Link
        to={detailPath}
        className="group grid md:grid-cols-2 gap-6 md:gap-8 items-center rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 md:p-8 hover:border-primary/40 transition-colors"
      >
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Calendar className="h-3 w-3" aria-hidden="true" />
            {workshop.date}
          </span>
          <h3 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-primary transition-colors">
            {workshop.title}
          </h3>
          <p className="text-muted-foreground mb-4">{summarize(workshop)}</p>
          {workshop.speaker && (
            <p className="text-sm font-medium text-foreground/80 mb-5">
              Led by {workshop.speaker}
            </p>
          )}
          <span className="inline-flex items-center gap-2 font-semibold text-primary">
            Workshop details &amp; registration
            <ArrowRight
              className="h-4 w-4 group-hover:translate-x-1 transition-transform"
              aria-hidden="true"
            />
          </span>
        </div>

        {workshop.featuredImage && (
          <div className="rounded-xl overflow-hidden border border-border/50 bg-muted/30">
            <img
              src={workshop.featuredImage}
              srcSet={buildWebpSrcSet(workshop.featuredImage)}
              sizes="(min-width: 1024px) 420px, 100vw"
              alt={`${workshop.title} workshop cover`}
              className="w-full h-full object-cover aspect-video"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
      </Link>
    </motion.article>
  );
};

const PastWorkshopCard = ({ workshop }: { workshop: Workshop }) => {
  const hasDetailPage = Boolean(workshop.fullDescription);
  const detailPath = getWorkshopPath(workshop.slug);
  const cardContent = (
    <>
      {workshop.featuredImage && (
        <div className="aspect-video overflow-hidden bg-muted/40">
          <img
            src={workshop.featuredImage}
            srcSet={buildWebpSrcSet(workshop.featuredImage)}
            sizes="(min-width: 1024px) 380px, (min-width: 640px) 50vw, 100vw"
            alt={`${workshop.title} workshop cover`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs font-medium text-muted-foreground mb-2">
          {workshop.date}
        </p>
        <h3 className="text-lg font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">
          {workshop.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {workshop.shortDescription}
        </p>
        {workshop.speaker && (
          <p className="text-xs text-foreground/70 mb-4">
            Led by {workshop.speaker}
          </p>
        )}
        <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          {pastWorkshopCtaLabel(workshop, hasDetailPage)}
          <ArrowRight
            className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform"
            aria-hidden="true"
          />
        </span>
      </div>
    </>
  );

  const className =
    "group flex h-full flex-col overflow-hidden rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/40 transition-colors";

  return (
    <motion.article variants={fadeInUp} className="h-full">
      {hasDetailPage ? (
        <Link to={detailPath} className={className}>
          {cardContent}
        </Link>
      ) : (
        <a
          href={
            workshop.registrationLink ??
            "https://members.aiforhrmastermind.com/memberships"
          }
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {cardContent}
        </a>
      )}
    </motion.article>
  );
};

export default Workshops;
